"use client";

import { CreditCard, Hash, Wallet, X } from "lucide-react";
import {
  DEFAULT_PAYMENT_OPTIONS,
  type PaymentOption,
} from "@/lib/payment-providers";

export function PaymentProviderModal({
  isOpen,
  onClose,
  onSelectOption,
  loading,
  availableOptions = DEFAULT_PAYMENT_OPTIONS,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectOption: (option: PaymentOption) => void;
  loading: boolean;
  availableOptions?: PaymentOption[];
}) {
  if (!isOpen) return null;

  const isAvailable = (option: PaymentOption) => availableOptions.includes(option);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-6 animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 duration-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-grey-900">
            How would you like to pay?
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5 text-grey-500" />
          </button>
        </div>

        <div className="space-y-3">
          {isAvailable("bank") && (
            <button
              type="button"
              onClick={() => onSelectOption("bank")}
              disabled={loading}
              className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
            >
              <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center">
                <Wallet className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-grey-900">Transfer (Paystack)</p>
              </div>
            </button>
          )}

          {isAvailable("card") && (
            <button
              type="button"
              onClick={() => onSelectOption("card")}
              disabled={loading}
              className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
            >
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-grey-900">Card</p>
              </div>
            </button>
          )}

          {isAvailable("ussd") && (
            <button
              type="button"
              onClick={() => onSelectOption("ussd")}
              disabled={loading}
              className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
            >
              <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center">
                <Hash className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-grey-900">USSD</p>
              </div>
            </button>
          )}

          {isAvailable("pocket_app") && (
            <button
              type="button"
              onClick={() => onSelectOption("pocket_app")}
              disabled={loading}
              className="w-full flex items-center gap-4 p-4 border border-grey-200 rounded-lg hover:bg-grey-50 hover:border-green-300 transition-colors disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden">
                <img
                  src="/pocket.png"
                  alt="PocketApp"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-grey-900">PocketApp</p>
              </div>
            </button>
          )}
        </div>

        {loading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-grey-600">
            <div className="w-4 h-4 border-2 border-grey-300 border-t-green-500 rounded-full animate-spin" />
            <span className="text-sm">Processing...</span>
          </div>
        )}
      </div>
    </div>
  );
}
